import turfArea from "@turf/area";
import type { Geometry } from "geojson";
import type { FullElement, OsmElement } from "./osm.ts";

/** Longitude, latitude. */
type Coord = [number, number];

/**
 * Reconstructs an element's geometry from a `/full` API response. Returns null
 * when the response is missing the members it would need, which happens for
 * relations whose members are themselves relations.
 */
export function geometryOf({ element, members }: FullElement): Geometry | null {
  if (element.type === "node") {
    if (element.lon === undefined || element.lat === undefined) return null;
    return { type: "Point", coordinates: [element.lon, element.lat] };
  }

  if (element.type === "way") {
    const line = wayPoints(element, members);
    if (!line || line.length < 2) return null;
    if (isClosed(line) && line.length >= 4) return { type: "Polygon", coordinates: [line] };
    return { type: "LineString", coordinates: line };
  }

  const outerWays: Coord[][] = [];
  const innerWays: Coord[][] = [];
  for (const member of element.members ?? []) {
    if (member.type !== "way") continue;
    const way = members.get(`w${member.ref}`);
    const points = way && wayPoints(way, members);
    if (!points) continue;
    (member.role === "inner" ? innerWays : outerWays).push(points);
  }

  const polygons = buildPolygons(assembleRings(outerWays), assembleRings(innerWays));
  if (polygons.length === 0) return null;
  return { type: "MultiPolygon", coordinates: polygons };
}

/** Area in square meters. Zero for anything that isn't a polygon. */
export function areaOf(geometry: Geometry): number {
  if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") return 0;
  return turfArea(geometry);
}

function wayPoints(way: OsmElement, members: Map<string, OsmElement>): Coord[] | null {
  const out: Coord[] = [];
  for (const nodeId of way.nodes ?? []) {
    const node = members.get(`n${nodeId}`);
    if (!node || node.lon === undefined || node.lat === undefined) return null;
    out.push([node.lon, node.lat]);
  }
  return out.length ? out : null;
}

const samePoint = (a: Coord, b: Coord) => a[0] === b[0] && a[1] === b[1];

const first = (ring: Coord[]): Coord => ring[0] as Coord;
const last = (ring: Coord[]): Coord => ring[ring.length - 1] as Coord;

const isClosed = (ring: Coord[]) => ring.length > 1 && samePoint(first(ring), last(ring));

/**
 * Joins ways end to end into closed rings, the way a multipolygon relation's
 * members have to be assembled. Ways that never close are discarded.
 */
function assembleRings(ways: Coord[][]): Coord[][] {
  const remaining = ways.filter((way) => way.length > 1).map((way) => [...way]);
  const rings: Coord[][] = [];

  while (remaining.length) {
    let ring = remaining.pop() as Coord[];

    while (!isClosed(ring)) {
      const end = last(ring);
      const next = remaining.findIndex(
        (way) => samePoint(end, first(way)) || samePoint(end, last(way)),
      );
      if (next < 0) break;

      const [way] = remaining.splice(next, 1) as [Coord[]];
      const forward = samePoint(end, first(way));
      ring = ring.concat((forward ? way : [...way].reverse()).slice(1));
    }

    if (isClosed(ring) && ring.length >= 4) rings.push(ring);
  }

  return rings;
}

function buildPolygons(outers: Coord[][], inners: Coord[][]): Coord[][][] {
  const polygons = outers.map((outer) => [outer]);

  for (const inner of inners) {
    const parent = polygons.find((polygon) => contains(polygon[0] as Coord[], first(inner)));
    // An inner ring with no containing outer ring means broken tagging; drop it
    // rather than emit a polygon whose hole isn't inside its shell.
    if (parent) parent.push(inner);
  }

  return polygons;
}

/** Ray casting point in ring test, in coordinate space. */
function contains(ring: Coord[], point: Coord): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i] as Coord;
    const [xj, yj] = ring[j] as Coord;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
